/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import { Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shared catalog of social platforms — brand glyphs, labels, colors, and URL
 * builders. Used by both the profile editor's `SocialLinks` boxes and the
 * read-only icon row in `UserProfileModal`, so the glyph set lives in one place.
 */

/** Brand glyphs for the known social platforms (OG _v19 linkIcons). */
export const PLATFORM_ICONS: Record<string, string> = {
  linkedin:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>',
  instagram:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  tiktok:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.62z"/></svg>',
  youtube:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.12-2.12C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.58A3 3 0 0 0 .5 6.2C0 8.07 0 12 0 12s0 3.93.5 5.8a3 3 0 0 0 2.12 2.12C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.58a3 3 0 0 0 2.12-2.12C24 15.93 24 12 24 12s0-3.93-.5-5.8zM9.55 15.57V8.43L15.82 12l-6.27 3.57z"/></svg>',
  web: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
  x: '<svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>',
};

/** Linkable platforms (label drives the picker; id maps to icons + storage key). */
export const PLATFORMS: { id: string; label: string }[] = [
  { id: 'x', label: 'X / Twitter' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'web', label: 'Website' },
];

export const PLATFORM_LABEL: Record<string, string> = Object.fromEntries(
  PLATFORMS.map((p) => [p.id, p.label]),
);

/**
 * Official brand colors.
 *  - `badge`  fills the icon chip (the glyph rides on top in white).
 *  - `accent` a single representative solid color used to tint borders/washes.
 * `web` is intentionally absent — a generic site stays neutral (favicon).
 */
export const PLATFORM_BRAND: Record<string, { badge: string; accent: string }> = {
  x: { badge: '#000000', accent: '#71767b' },
  instagram: {
    badge: 'linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)',
    accent: '#d62976',
  },
  tiktok: { badge: '#000000', accent: '#25f4ee' },
  youtube: { badge: '#ff0000', accent: '#ff0000' },
  linkedin: { badge: '#0a66c2', accent: '#0a66c2' },
};

/** Builds a profile URL from a username, per platform. `web` takes a raw URL. */
export const PLATFORM_URL: Record<string, (username: string) => string> = {
  x: (u) => `https://x.com/${u}`,
  instagram: (u) => `https://instagram.com/${u}`,
  tiktok: (u) => `https://tiktok.com/@${u}`,
  youtube: (u) => `https://youtube.com/@${u}`,
  linkedin: (u) => `https://linkedin.com/in/${u}`,
};

/**
 * Parse a pasted link into a real URL, prepending https:// for a bare host.
 * Returns null for anything that isn't a proper http(s) web link.
 */
export function parseUrl(raw: string): URL | null {
  const t = raw.trim();
  if (!t) return null;
  const candidate = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const url = new URL(candidate);
    const host = url.hostname;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return null;
    return url;
  } catch {
    return null;
  }
}

export function PlatformIcon({ id, className }: { id: string; className?: string }) {
  if (PLATFORM_ICONS[id]) {
    return (
      <span
        className={cn('inline-flex items-center justify-center [&>svg]:h-full [&>svg]:w-full', className)}
        aria-hidden
        dangerouslySetInnerHTML={{ __html: PLATFORM_ICONS[id] }}
      />
    );
  }
  return <Link2 size={16} />;
}

/**
 * A website's own favicon, pulled from its host, with the globe glyph as the
 * fallback when the site has none (or the lookup fails). Only used for `web`.
 */
export function LinkFavicon({ href, className }: { href: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const host = parseUrl(href)?.hostname;
  if (failed || !host) return <PlatformIcon id="web" className={className} />;
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${host}&sz=128`}
      alt=""
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}
